import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { CaretDown, Check, MagnifyingGlass, X, Translate } from "@phosphor-icons/react";
import { LANGUAGE_CODES, BOSNIAN_CODE, MAX_ADDITIONAL_LANGUAGES } from "@/lib/languages";

interface LanguageMultiComboboxProps {
  /** Selected *additional* languages (Bosnian is implicit; never present in this array). */
  value: string[];
  onChange: (codes: string[]) => void;
  disabled?: boolean;
  className?: string;
}

export function LanguageMultiCombobox({
  value,
  onChange,
  disabled = false,
  className,
}: LanguageMultiComboboxProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const selectedSet = useMemo(() => new Set(value), [value]);
  const capReached = value.length >= MAX_ADDITIONAL_LANGUAGES;

  const options = useMemo(() => {
    const list = LANGUAGE_CODES
      .filter((c) => c !== BOSNIAN_CODE)
      .map((c) => ({ code: c, name: t(`languages.names.${c}`) }));
    list.sort((a, b) => a.name.localeCompare(b.name));
    return list;
  }, [t]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.name.toLowerCase().includes(q) || o.code.includes(q));
  }, [options, query]);

  function toggle(code: string) {
    if (selectedSet.has(code)) {
      onChange(value.filter((c) => c !== code));
    } else {
      if (capReached) return;
      onChange([...value, code]);
    }
  }

  function remove(code: string) {
    onChange(value.filter((c) => c !== code));
  }

  const selectedSorted = useMemo(() => {
    return [...value].sort((a, b) =>
      t(`languages.names.${a}`).localeCompare(t(`languages.names.${b}`))
    );
  }, [value, t]);

  return (
    <div className={cn("space-y-2", className)}>
      <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQuery(""); }}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            className={cn(
              "inline-flex h-9 w-full items-center gap-2 rounded-md border border-input bg-background px-3 text-sm transition-colors",
              "hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              disabled && "cursor-not-allowed opacity-50"
            )}
          >
            <Translate className="size-4 text-muted-foreground" />
            <span className="flex-1 truncate text-left text-muted-foreground">
              {capReached
                ? t("languages.pickerPlaceholder") + ` (${value.length}/${MAX_ADDITIONAL_LANGUAGES})`
                : t("languages.pickerPlaceholder")}
            </span>
            <CaretDown className="size-3.5 shrink-0 opacity-60" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-72 p-0">
          <div className="border-b border-border/40 p-2">
            <div className="relative">
              <MagnifyingGlass className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder={t("languages.pickerSearch")}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="h-8 pl-8 text-xs"
                autoFocus
              />
            </div>
          </div>
          <div className="max-h-64 overflow-y-auto p-1">
            {filtered.length === 0 ? (
              <p className="px-2 py-6 text-center text-xs text-muted-foreground">—</p>
            ) : (
              filtered.map((o) => {
                const active = selectedSet.has(o.code);
                const dim = !active && capReached;
                return (
                  <button
                    key={o.code}
                    type="button"
                    onClick={() => toggle(o.code)}
                    disabled={dim}
                    className={cn(
                      "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors",
                      active
                        ? "bg-primary/[0.06] text-foreground"
                        : "text-muted-foreground hover:bg-muted/40 hover:text-foreground",
                      dim && "cursor-not-allowed opacity-40 hover:bg-transparent"
                    )}
                  >
                    <span className="flex-1 truncate font-medium">{o.name}</span>
                    <span className="text-[10px] text-muted-foreground">{o.code}</span>
                    {active && <Check className="size-3.5 shrink-0 text-primary" />}
                  </button>
                );
              })
            )}
          </div>
        </PopoverContent>
      </Popover>

      <div className="flex flex-wrap gap-1.5">
        <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-0.5 text-[11px] font-medium text-muted-foreground">
          {t("languages.bosnianLocked")}
        </span>
        {selectedSorted.map((code) => (
          <span
            key={code}
            className="inline-flex items-center gap-1 rounded-full bg-sky-100 px-2.5 py-0.5 text-[11px] font-medium text-sky-800"
          >
            {t(`languages.names.${code}`)}
            <button
              type="button"
              onClick={() => remove(code)}
              className="ml-0.5 rounded-full p-0.5 hover:bg-sky-200"
              aria-label={`Remove ${code}`}
            >
              <X className="size-2.5" />
            </button>
          </span>
        ))}
      </div>
    </div>
  );
}
